import { createRoot } from "react-dom/client";
import "@mantine/core/styles.css";
import "@mantine/notifications/styles.css";
import "@mantine/code-highlight/styles.css";
import "highlight.js/styles/github.css";
import { CodeHighlightAdapterProvider, createHighlightJsAdapter } from "@mantine/code-highlight";
import { createTheme, MantineProvider } from "@mantine/core";
import { ModalsProvider } from "@mantine/modals";
import { Notifications } from "@mantine/notifications";
import * as Sentry from "@sentry/react";
import hljs from "highlight.js/lib/core";
import bash from "highlight.js/lib/languages/bash";
import css from "highlight.js/lib/languages/css";
import go from "highlight.js/lib/languages/go";
import graphql from "highlight.js/lib/languages/graphql";
import javascript from "highlight.js/lib/languages/javascript";
import json from "highlight.js/lib/languages/json";
import plaintext from "highlight.js/lib/languages/plaintext";
import python from "highlight.js/lib/languages/python";
import rust from "highlight.js/lib/languages/rust";
import scss from "highlight.js/lib/languages/scss";
import ts from "highlight.js/lib/languages/typescript";
import xml from "highlight.js/lib/languages/xml";
import { BrowserRouter } from "react-router-dom";
import { createClient as createWsClient } from "graphql-ws";
import { Client, cacheExchange, fetchExchange, Provider as UrqlProvider, subscriptionExchange } from "urql";
import App from "./App";
import { UserCurrentProvider } from "./CurrentUser";
import { SiteConfigProvider } from "./SiteConfig";

const wsProtocol = window.location.protocol === "https:" ? "wss" : "ws";
const wsClient = createWsClient({
    url: `${wsProtocol}://${window.location.host}/graphql`,
});

const client = new Client({
    url: "/graphql",
    exchanges: [
        cacheExchange,
        fetchExchange,
        subscriptionExchange({
            forwardSubscription(request) {
                const input = { ...request, query: request.query || "" };
                return {
                    subscribe(sink) {
                        const unsubscribe = wsClient.subscribe(input, sink);
                        return { unsubscribe };
                    },
                };
            },
        }),
    ],
});

const theme = createTheme({
    /** Put your mantine theme override here */
});

hljs.registerLanguage("typescript", ts);
hljs.registerLanguage("javascript", javascript);
hljs.registerLanguage("bash", bash);
hljs.registerLanguage("json", json);
hljs.registerLanguage("css", css);
hljs.registerLanguage("scss", scss);
hljs.registerLanguage("xml", xml);
hljs.registerLanguage("html", xml);
hljs.registerLanguage("go", go);
hljs.registerLanguage("python", python);
hljs.registerLanguage("graphql", graphql);
hljs.registerLanguage("rust", rust);
hljs.registerLanguage("plaintext", plaintext);

const highlightJsAdapter = createHighlightJsAdapter(hljs);

function init() {
    Sentry.init({
        dsn: window.__CONFIG__.SENTRY_DSN,
        release: window.__CONFIG__.RELEASE,
    });

    try {
        const container = document.getElementById("root");
        if (!container) {
            throw new Error("Failed to find the root element");
        }
        const root = createRoot(container);
        root.render(
            <MantineProvider theme={theme}>
                <CodeHighlightAdapterProvider adapter={highlightJsAdapter}>
                    <UrqlProvider value={client}>
                        <ModalsProvider>
                            <BrowserRouter>
                                <Sentry.ErrorBoundary fallback={<p>"An error has occurred"</p>}>
                                    <SiteConfigProvider>
                                        <UserCurrentProvider>
                                            <Notifications />
                                            <App />
                                        </UserCurrentProvider>
                                    </SiteConfigProvider>
                                </Sentry.ErrorBoundary>
                            </BrowserRouter>
                        </ModalsProvider>
                    </UrqlProvider>
                </CodeHighlightAdapterProvider>
            </MantineProvider>,
        );
    } catch (error) {
        console.error(error);
        Sentry.captureException(error);
    }
}

init();
